import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { initiativesAPI } from '../../lib/api';

export default function AdminInitiatives() {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInitiatives = async () => {
    try {
      const response = await initiativesAPI.getAll();
      setInitiatives(response.data);
    } catch (error) {
      console.error('Failed to fetch initiatives:', error);
      toast.error('Failed to load initiatives');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitiatives();
  }, []);

  const handleDelete = async (id) => {
    try {
      await initiativesAPI.delete(id);
      toast.success('Initiative deleted successfully');
      fetchInitiatives();
    } catch (error) {
      toast.error('Failed to delete initiative');
    }
  };

  const statusColors = {
    ongoing: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    upcoming: 'bg-amber-100 text-amber-800'
  };

  return (
    <div data-testid="admin-initiatives">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Initiatives
          </h1>
          <p className="text-muted-foreground">
            Manage heritage projects and initiatives.
          </p>
        </div>
        <Link to="/admin/initiatives/new">
          <Button data-testid="new-initiative-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Initiative
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-background p-4 border border-border animate-pulse">
              <div className="h-6 bg-muted w-1/3 mb-2"></div>
              <div className="h-4 bg-muted w-1/2"></div>
            </div>
          ))}
        </div>
      ) : initiatives.length > 0 ? (
        <div className="space-y-4">
          {initiatives.map((initiative) => (
            <div key={initiative.id} className="bg-background p-6 border border-border flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    {initiative.title}
                  </h3>
                  <span className={`px-2 py-0.5 text-xs font-medium uppercase ${statusColors[initiative.status] || statusColors.ongoing}`}>
                    {initiative.status}
                  </span>
                </div>
                <p className="text-muted-foreground line-clamp-2">
                  {initiative.description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/admin/initiatives/${initiative.id}/edit`}>
                  <Button variant="ghost" size="sm">
                    <Edit className="h-4 w-4" />
                  </Button>
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Initiative</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{initiative.title}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(initiative.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-background border border-border p-12 text-center">
          <h3 className="font-heading text-xl text-foreground mb-2">No Initiatives Yet</h3>
          <p className="text-muted-foreground mb-6">Create your first heritage initiative.</p>
          <Link to="/admin/initiatives/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Initiative
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
